import { useInView } from "react-intersection-observer";
import dynamic, { LoaderComponent } from "next/dynamic";

interface LazyOptions {
  /**
   * The JSX element, if any, to render before the dynamicImport has
   * loaded.
   */
  loading?: JSX.Element;
  /**
   * The intersection margin in pixels.
   */
  rootMargin?: string;
}

/**
 * A utility that returns a component that conditionally loads based on an
 * IntersectionObserver. Usage is similar to next/dynamic and React.lazy
 *
 * @example
 * const MyDynamicComponent = () => import(
 *   "MyComponent",
 *   { loading: <div>Loading...</div> }
 * );
 * return <MyDynamicComponent />
 */
const lazy = <T extends {}>(
  /**
   * A callable that returns a dynamically imported module
   * (i.e. one that uses the import() function)
   *
   * @example
   * const myDynamicComponent = () => import("MyComponent");
   */
  dynamicImport: () => LoaderComponent<T>,
  options?: LazyOptions
) => {
  const opts: LazyOptions = {
    rootMargin: "500px",
    ...options,
  };

  const Lazy = (props: T) => {
    const Component = dynamic<T>(dynamicImport, {
      loading: () => opts.loading ?? null,
      ssr: false,
    });

    const [sentinel, inView] = useInView({
      rootMargin: opts.rootMargin,
      threshold: 0,
      triggerOnce: true,
    });

    return (
      <div ref={sentinel}>
        {inView ? <Component {...(props as T)} /> : null}
      </div>
    );
  };
  return Lazy;
};

export default lazy;
